import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { PlusIcon, TargetIcon } from 'lucide-react'
import { useEffect, useState } from 'react'

import { PROSPECTING_KEY, prospectingApi, type ProspectListParams } from '@/api/prospecting'
import type {
  Prospect,
  ProspectingProgram,
  ProspectSource,
  ProspectStatus,
  ProspectTier,
} from '@/api/types'
import { EmptyState } from '@/components/empty-state'
import { FormError } from '@/components/field-error'
import { FilterBar, FilterField } from '@/components/filter-bar'
import { Frame } from '@/components/frame'
import { PageHeader } from '@/components/page-header'
import { Pagination } from '@/components/pagination'
import { ProspectTierBadge } from '@/components/prospecting/prospect-tier-badge'
import { NewProgramDialog, NewProspectDialog } from '@/components/prospecting/prospecting-dialogs'
import { SearchInput } from '@/components/search-input'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useDebouncedValue } from '@/hooks/use-debounced-value'
import { useOrigin } from '@/hooks/use-return-to'
import { formatDateOnly, formatRelative } from '@/lib/format'
import { PROSPECT_STATUSES, PROSPECT_TIERS } from '@/lib/orders'
import { authorName } from '@/lib/people'
import { localToday } from '@/lib/portal-home'
import {
  PROSPECT_SOURCE_LABELS,
  PROSPECT_STATUS_LABELS,
  PROSPECT_TIER_LABELS,
} from '@/lib/prospecting'
import { integer, oneOf, readSearch, text } from '@/lib/search-params'

const SHAPE = {
  /** « À relancer » : les prospects ouverts dont l'action est due. */
  affichage: oneOf(['prospects', 'relancer'] as const),
  q: text(),
  programme: text(),
  classe: oneOf(PROSPECT_TIERS),
  statut: oneOf(PROSPECT_STATUSES),
  /** `me`, `none`, ou un id. */
  responsable: text(),
  source: oneOf(['meta_form', 'landing_page', 'manual'] as const),
  classement: oneOf(['recus', 'score'] as const),
  page: integer({ min: 1 }),
}

export const Route = createFileRoute('/_app/prospecting/')({
  validateSearch: (search: Record<string, unknown>) => readSearch(SHAPE, search),
  component: ProspectingPage,
})

const ALL = '__tous__'
const OPEN: ProspectStatus[] = ['new', 'contacted', 'meeting']

/**
 * Prospection (lot 43, contrat 1.86.0) : les programmes, puis les prospects
 * — classe en pastille, score, statut, responsable, campagne, date —, filtrés
 * et triés par l'adresse ; « À relancer » réunit les prospects ouverts dont la
 * prochaine action est due, la plus ancienne d'abord.
 */
function ProspectingPage() {
  const raw = Route.useSearch()
  // Les valeurs par défaut vivent ici : un lien vers « /prospecting » n'a rien à porter.
  const search = {
    ...raw,
    affichage: raw.affichage ?? 'prospects',
    classement: raw.classement ?? 'recus',
    page: raw.page ?? 1,
  }
  const navigate = Route.useNavigate()
  const set = (patch: Partial<typeof search>) => {
    void navigate({ search: (current) => ({ ...current, page: 1, ...patch }) })
  }
  const [query, setQuery] = useState(search.q ?? '')
  const q = useDebouncedValue(query)
  // La recherche tapée rejoint l'adresse une fois la frappe posée.
  useEffect(() => {
    if ((q || undefined) !== search.q) {
      void navigate({ search: (current) => ({ ...current, page: 1, q: q || undefined }) })
    }
  }, [q, search.q, navigate])
  const [creatingProgram, setCreatingProgram] = useState(false)
  const [creatingProspect, setCreatingProspect] = useState(false)

  const programs = useQuery({
    queryKey: [...PROSPECTING_KEY, 'programs'],
    queryFn: () => prospectingApi.programs(),
  })
  const programItems = programs.data ?? []
  const followUp = search.affichage === 'relancer'
  const params: ProspectListParams = {
    q: search.q,
    program_id: search.programme,
    tier: search.classe,
    status: search.statut,
    owner_id: search.responsable,
    source: search.source,
    sort: search.classement === 'score' ? '-score' : '-received_at',
    page: followUp ? 1 : search.page,
    page_size: followUp ? 200 : 50,
  }
  const prospects = useQuery({
    queryKey: [...PROSPECTING_KEY, 'prospects', params],
    queryFn: () => prospectingApi.prospects(params),
    placeholderData: (previous) => previous,
  })
  const today = localToday()
  const items = (prospects.data?.items ?? []).filter(
    (prospect) =>
      !followUp ||
      (prospect.next_action_at !== null &&
        prospect.next_action_at <= today &&
        OPEN.includes(prospect.status)),
  )
  if (followUp) {
    items.sort((a, b) => (a.next_action_at ?? '').localeCompare(b.next_action_at ?? ''))
  }
  const programName = programItems.find((program) => program.id === search.programme)?.name

  return (
    <div className="space-y-6" data-testid="prospection">
      <PageHeader
        title="Prospection"
        actions={
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setCreatingProgram(true)
              }}
            >
              <PlusIcon />
              Nouveau programme
            </Button>
            <Button
              disabled={programItems.length === 0}
              onClick={() => {
                setCreatingProspect(true)
              }}
            >
              <PlusIcon />
              Ajouter un prospect
            </Button>
          </div>
        }
      />

      <ProgramStrip programs={programItems} pending={programs.isPending} />

      <Frame className="space-y-4">
        <Tabs
          value={search.affichage}
          onValueChange={(vue) => {
            set({ affichage: vue as typeof search.affichage })
          }}
        >
          <TabsList>
            <TabsTrigger value="prospects">Tous les prospects</TabsTrigger>
            <TabsTrigger value="relancer" data-testid="vue-relancer">
              À relancer
            </TabsTrigger>
          </TabsList>
        </Tabs>
        <FilterBar
          search={
            <SearchInput
              value={query}
              onChange={setQuery}
              label="Rechercher un prospect"
              placeholder="Nom, adresse, entreprise, téléphone…"
              className="w-full max-w-xs"
            />
          }
          active={[
            ...(search.programme
              ? [
                  {
                    key: 'programme',
                    label: programName ?? 'Programme',
                    onClear: () => {
                      set({ programme: undefined })
                    },
                  },
                ]
              : []),
            ...(search.classe
              ? [
                  {
                    key: 'classe',
                    label: PROSPECT_TIER_LABELS[search.classe],
                    onClear: () => {
                      set({ classe: undefined })
                    },
                  },
                ]
              : []),
            ...(search.statut
              ? [
                  {
                    key: 'statut',
                    label: PROSPECT_STATUS_LABELS[search.statut],
                    onClear: () => {
                      set({ statut: undefined })
                    },
                  },
                ]
              : []),
            ...(search.responsable
              ? [
                  {
                    key: 'responsable',
                    label:
                      search.responsable === 'me'
                        ? 'Les miens'
                        : search.responsable === 'none'
                          ? 'Sans responsable'
                          : 'Un responsable',
                    onClear: () => {
                      set({ responsable: undefined })
                    },
                  },
                ]
              : []),
            ...(search.source
              ? [
                  {
                    key: 'source',
                    label: PROSPECT_SOURCE_LABELS[search.source],
                    onClear: () => {
                      set({ source: undefined })
                    },
                  },
                ]
              : []),
            ...(search.classement === 'score'
              ? [
                  {
                    key: 'classement',
                    label: 'Triés par score',
                    onClear: () => {
                      set({ classement: 'recus' })
                    },
                  },
                ]
              : []),
          ]}
          onClearAll={() => {
            set({
              programme: undefined,
              classe: undefined,
              statut: undefined,
              responsable: undefined,
              source: undefined,
              classement: 'recus',
            })
          }}
        >
          <Choice
            label="Programme"
            value={search.programme}
            options={programItems.map((program) => [program.id, program.name] as const)}
            onChange={(programme) => {
              set({ programme })
            }}
          />
          <Choice
            label="Classe"
            value={search.classe}
            options={PROSPECT_TIERS.map((tier) => [tier, PROSPECT_TIER_LABELS[tier]] as const)}
            onChange={(classe) => {
              set({ classe: classe as ProspectTier | undefined })
            }}
          />
          <Choice
            label="Statut"
            value={search.statut}
            options={PROSPECT_STATUSES.map(
              (status) => [status, PROSPECT_STATUS_LABELS[status]] as const,
            )}
            onChange={(statut) => {
              set({ statut: statut as ProspectStatus | undefined })
            }}
          />
          <Choice
            label="Responsable"
            value={search.responsable}
            options={[
              ['me', 'Les miens'],
              ['none', 'Sans responsable'],
            ]}
            onChange={(responsable) => {
              set({ responsable })
            }}
          />
          <Choice
            label="Source"
            value={search.source}
            options={(Object.keys(PROSPECT_SOURCE_LABELS) as ProspectSource[]).map(
              (source) => [source, PROSPECT_SOURCE_LABELS[source]] as const,
            )}
            onChange={(source) => {
              set({ source: source as ProspectSource | undefined })
            }}
          />
          <FilterField label="Tri">
            <Select
              value={search.classement}
              onValueChange={(tri) => {
                set({ classement: tri as typeof search.classement })
              }}
            >
              <SelectTrigger aria-label="Trier les prospects">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="recus">Les plus récents</SelectItem>
                <SelectItem value="score">Le meilleur score</SelectItem>
              </SelectContent>
            </Select>
          </FilterField>
        </FilterBar>

        {prospects.isPending ? (
          <Skeleton className="h-48 w-full" />
        ) : prospects.isError ? (
          <FormError>Les prospects n’ont pas pu être lus.</FormError>
        ) : items.length === 0 ? (
          <EmptyState
            icon={TargetIcon}
            title={followUp ? 'Personne à relancer aujourd’hui' : 'Aucun prospect'}
            description={
              followUp
                ? 'Une prochaine action posée sur un prospect le fait apparaître ici le jour venu.'
                : 'Les leads des formulaires Meta et de la page de qualification arrivent ici, classés.'
            }
          />
        ) : (
          <ProspectsTable prospects={items} followUp={followUp} />
        )}
        {!followUp && prospects.data && prospects.data.total > prospects.data.page_size && (
          <Pagination
            page={prospects.data.page}
            pageSize={prospects.data.page_size}
            total={prospects.data.total}
            itemLabel="prospect"
            onPageChange={(page) => {
              void navigate({ search: (current) => ({ ...current, page }) })
            }}
          />
        )}
      </Frame>

      <NewProgramDialog open={creatingProgram} onOpenChange={setCreatingProgram} />
      <NewProspectDialog
        open={creatingProspect}
        onOpenChange={setCreatingProspect}
        programs={programItems}
      />
    </div>
  )
}

function Choice({
  label,
  value,
  options,
  onChange,
}: {
  label: string
  value: string | undefined
  options: readonly (readonly [string, string])[]
  onChange: (value: string | undefined) => void
}) {
  return (
    <FilterField label={label}>
      <Select
        value={value ?? ALL}
        onValueChange={(next) => {
          onChange(next === ALL ? undefined : next)
        }}
      >
        <SelectTrigger aria-label={`Filtrer par ${label.toLowerCase()}`}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL}>Tous</SelectItem>
          {options.map(([key, text]) => (
            <SelectItem key={key} value={key}>
              {text}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </FilterField>
  )
}

/** Les programmes en cartes : le compte par classe, et le lien vers chacun. */
function ProgramStrip({
  programs,
  pending,
}: {
  programs: readonly ProspectingProgram[]
  pending: boolean
}) {
  const origin = useOrigin()
  if (pending) return <Skeleton className="h-24 w-full" />
  if (programs.length === 0) {
    return (
      <Frame>
        <EmptyState
          icon={TargetIcon}
          title="Aucun programme"
          description="Un programme décrit ce qu’on vend et à qui : sa grille de qualification classe chaque prospect qui répond."
        />
      </Frame>
    )
  }
  return (
    <ul className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3" data-testid="programmes">
      {programs.map((program) => (
        <li key={program.id}>
          <Link
            to="/prospecting/programs/$programId"
            params={{ programId: program.short_id }}
            search={origin}
            className="block space-y-2 rounded-lg border bg-card p-4 hover:bg-muted/40 focus-visible:outline-2 focus-visible:outline-ring"
            data-testid="carte-programme"
          >
            <span className="flex items-center justify-between gap-2">
              <span className="font-medium">{program.name}</span>
              {program.status !== 'active' && (
                <Badge variant="secondary">
                  {program.status === 'paused' ? 'En pause' : 'Archivé'}
                </Badge>
              )}
            </span>
            <span className="flex flex-wrap gap-1.5 text-xs">
              {PROSPECT_TIERS.map((tier) => (
                <span key={tier} className="inline-flex items-center gap-1">
                  <ProspectTierBadge tier={tier} />
                  <span className="tabular-nums">{program.counts[tier]}</span>
                </span>
              ))}
            </span>
            <span className="block text-xs text-muted-foreground">
              {program.counts.total} prospect{program.counts.total > 1 ? 's' : ''} ·{' '}
              {program.counts.converted} converti{program.counts.converted > 1 ? 's' : ''}
            </span>
          </Link>
        </li>
      ))}
    </ul>
  )
}

function ProspectsTable({ prospects, followUp }: { prospects: Prospect[]; followUp: boolean }) {
  const origin = useOrigin()
  return (
    <div className="overflow-x-auto rounded-lg border">
      <table className="w-full text-sm" data-testid="liste-prospects">
        <thead className="bg-muted/40 text-left text-xs text-muted-foreground">
          <tr>
            <th className="px-3 py-2 font-medium">Prospect</th>
            <th className="px-3 py-2 font-medium">Classe</th>
            <th className="px-3 py-2 text-right font-medium">Score</th>
            <th className="px-3 py-2 font-medium">Statut</th>
            <th className="px-3 py-2 font-medium">Responsable</th>
            <th className="px-3 py-2 font-medium">Campagne</th>
            <th className="px-3 py-2 font-medium">{followUp ? 'À relancer le' : 'Reçu'}</th>
          </tr>
        </thead>
        <tbody className="divide-y">
          {prospects.map((prospect) => (
            <tr key={prospect.id} data-testid="ligne-prospect">
              <td className="px-3 py-2">
                <Link
                  to="/prospecting/prospects/$prospectId"
                  params={{ prospectId: prospect.short_id }}
                  search={origin}
                  className="font-medium text-primary underline-offset-2 hover:underline"
                >
                  {prospect.full_name ?? prospect.email ?? prospect.short_id}
                </Link>
                {prospect.company && (
                  <span className="block text-xs text-muted-foreground">{prospect.company}</span>
                )}
              </td>
              <td className="px-3 py-2">
                <ProspectTierBadge
                  tier={prospect.tier}
                  corrected={prospect.tier_override !== null}
                />
              </td>
              <td className="px-3 py-2 text-right tabular-nums">{prospect.score}</td>
              <td className="px-3 py-2">{PROSPECT_STATUS_LABELS[prospect.status]}</td>
              <td className="px-3 py-2 text-muted-foreground">
                {prospect.owner ? authorName(prospect.owner) : '—'}
              </td>
              <td className="px-3 py-2 text-muted-foreground">
                {prospect.attribution.meta_campaign_name ?? PROSPECT_SOURCE_LABELS[prospect.source]}
              </td>
              <td className="px-3 py-2 text-muted-foreground">
                {followUp && prospect.next_action_at
                  ? formatDateOnly(prospect.next_action_at)
                  : formatRelative(prospect.received_at)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
